import { Waves } from 'lucide-react'
import { VISIBLE_FISHES, TOTAL_COLLECTION, type FishDef } from '@/data/fish'

interface RosterPanelProps {
  selectedId: string
  onSelect: (fish: FishDef) => void
  unlockedFish: Set<string>
}

export function RosterPanel({ selectedId, onSelect, unlockedFish }: RosterPanelProps) {
  return (
    <aside className="flex flex-col h-full gap-4">
      {/* Logo */}
      <div className="flex items-center gap-3 px-1 pt-1">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center shadow-lg shadow-sky-300/50">
          <Waves className="text-white" size={22} />
        </div>
        <div>
          <h1 className="text-xl font-black tracking-[0.2em] text-[#2f5d8a]">灵境水族</h1>
          <p className="text-[10px] tracking-[0.3em] text-slate-400 font-semibold">SELECT YOUR FISH · 选择伙伴</p>
        </div>
      </div>

      {/* 鱼列表 */}
      <div className="flex-1 flex flex-col gap-3 overflow-y-auto pr-1 min-h-0">
        {VISIBLE_FISHES.map((f) => {
          const active = f.id === selectedId
          return (
            <button
              key={f.id}
              onClick={() => onSelect(f)}
              className={`group relative flex items-center gap-3 rounded-2xl border p-3 text-left transition-all duration-300 backdrop-blur-md ${
                active
                  ? 'bg-white/85 border-sky-300 shadow-[0_8px_28px_rgba(70,140,220,0.25)] scale-[1.02]'
                  : 'bg-white/45 border-white/70 hover:bg-white/70 hover:scale-[1.01]'
              }`}
            >
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden shrink-0 transition-colors ${active ? 'bg-sky-100' : 'bg-slate-100/80'}`}>
                <img src={f.img} alt={f.name} className="w-12 h-12 object-contain drop-shadow" draggable={false} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`font-black tracking-wider ${active ? 'text-[#2f5d8a]' : 'text-slate-600'}`}>{f.code}</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: f.traitColor }} />
                  <span className="truncate">{f.name} · {f.species} · {f.trait}</span>
                </div>
              </div>
              {active && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold tracking-widest text-white bg-gradient-to-r from-sky-400 to-blue-500 rounded-full px-2.5 py-1 shadow">
                  在缸中
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* 收集进度 */}
      <div className="rounded-2xl bg-white/50 border border-white/70 backdrop-blur-md p-3.5">
        <div className="flex items-center justify-between text-xs font-semibold text-slate-500 mb-2">
          <span className="tracking-widest">图鉴收集 COLLECTION</span>
          <span className="text-[#2f5d8a] font-black">{unlockedFish.size}/{TOTAL_COLLECTION}</span>
        </div>
        <div className="flex gap-1">
          {Array.from({ length: TOTAL_COLLECTION }, (_, i) => (
            <span
              key={i}
              className={`h-1.5 flex-1 rounded-full ${i < unlockedFish.size ? 'bg-gradient-to-r from-sky-400 to-blue-500' : 'bg-slate-300/50'}`}
            />
          ))}
        </div>
      </div>
    </aside>
  )
}
